import { Formik, FormikValues } from 'formik';
import React, { ChangeEvent, useContext, useEffect, useState } from 'react';
import { ActivityIndicatorContext } from '../../../providers/ActivityIndicatorProvider/context';
import { Components } from '../CreateOrEditLessonModal/styled';
import ProjectTextField from '../../inputs/ProjectTextField';
import ProjectSelect from '../../inputs/ProjectSelect';
import { ToastContext } from '../../../providers/ToastProvider/context';
import useModalState from '../../../hooks/useModalState';
import { getProjectErrorFromRawError, isProjectError } from '../../../utils/errors';
import ProjectModal from '../../ProjectModal';
import ConfirmationModal from '../ConfirmationModal';
import { confirmationTexts } from '../ConfirmationModal/utils';
import { textFieldUtils } from '../../../utils/textFields';
import { getPresignedUrl, uploadPrivateFile, uploadPublicFile } from '../../../utils/s3';
import { getFileExtension } from '../../../utils/s3/utils';
import { PUBLIC_BUCKET } from '../../../defines';
import { ReactComponent as AccessLinkIcon } from '../../../assets/icons/icon-grey-check-square-offset.svg';
import { Author } from '../../../api/types/author';
import { MentorShort } from '../../../api/types/mentorShort';
import { MinimalMentorShortSchema } from '../../../api/mentorShort/parser';
import { CreateMentorShortDto } from '../../../api/typeDtos/mentorShort';
import { createMentorShort, getNextMentorShortOrder, updateMentorShort } from '../../../api/mentorShort';

// "ms" = mentor short, mirroring getLessonUploadFileName's prefix-randomId keys.
const mentorShortFileName = () => 'ms-'.concat(Math.random().toString(36).substring(2, 10));

// Reads a local video file's length (seconds) before upload, so admins don't
// have to type it.
const readVideoDuration = (file: File): Promise<number | undefined> =>
  new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.onloadedmetadata = () => {
      URL.revokeObjectURL(url);
      resolve(Number.isFinite(video.duration) ? Math.round(video.duration) : undefined);
    };
    video.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(undefined);
    };
    video.src = url;
  });

const ModalBody = ({
  modalState,
  authors,
  defaultAuthorId,
  lockAuthor,
  onSave,
  onCancel,
}: {
  modalState?: MentorShort;
  authors: Author[];
  defaultAuthorId?: string;
  lockAuthor?: boolean;
  onSave: (values: FormikValues, media: { videoStreamPath?: string; thumbnailPath?: string }) => Promise<void>;
  onCancel: () => void;
}) => {
  const { setIsLoading } = useContext(ActivityIndicatorContext);
  const [videoPath, setVideoPath] = useState<string | undefined>(modalState?.videoStreamPath);
  const [videoProgress, setVideoProgress] = useState<number | undefined>(undefined);
  const [thumbnailPath, setThumbnailPath] = useState<string | undefined>(modalState?.thumbnailPath ?? undefined);
  const [thumbnailProgress, setThumbnailProgress] = useState<number | undefined>(undefined);

  const initialValues = {
    authorId: modalState?.authorId ?? defaultAuthorId ?? '',
    title: modalState?.title ?? '',
    durationSeconds: modalState?.durationSeconds ?? '',
    isActive: modalState?.isActive ?? true,
  };

  const onSelectVideo = async (
    event: ChangeEvent<HTMLInputElement>,
    setFieldValue: (field: string, value: unknown) => void,
  ) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }
    const duration = await readVideoDuration(file);
    if (duration) {
      setFieldValue('durationSeconds', duration);
    }
    const fileName = mentorShortFileName();
    const path = 'video/'.concat(fileName, '.', getFileExtension(file) || 'mp4');
    const renamed = new File([file], fileName, { type: file.type });
    event.target.value = '';
    setIsLoading(true);
    await uploadPrivateFile(renamed, path, (progress) => {
      setVideoProgress(progress);
      if (progress === 100) {
        setVideoPath(path);
        setVideoProgress(undefined);
      }
    });
    setIsLoading(false);
  };

  const onSelectThumbnail = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }
    const fileName = mentorShortFileName();
    const path = 'images/'.concat(fileName, '.', getFileExtension(file) || 'jpg');
    const renamed = new File([file], fileName, { type: file.type });
    event.target.value = '';
    setIsLoading(true);
    await uploadPublicFile(renamed, path, (progress) => {
      setThumbnailProgress(progress);
      if (progress === 100) {
        setThumbnailPath(path);
        setThumbnailProgress(undefined);
      }
    });
    setIsLoading(false);
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={MinimalMentorShortSchema}
      onSubmit={(values) => {
        const media = { videoStreamPath: videoPath, thumbnailPath };
        return onSave({ ...values, durationSeconds: Number(values.durationSeconds) }, media);
      }}>
      {({ values, handleChange, errors, touched, setFieldValue }) => (
        <Components.Form>
          <Components.ModalBodyContainer>
            <Components.FieldsWrapper>
              <Components.FieldStartAdornment variant={'minimal'} />
              <Components.FieldsContent>
                <Components.FieldWrapper>
                  <Components.FieldLabel>Mentor</Components.FieldLabel>
                  <ProjectSelect
                    value={values.authorId}
                    processShownLabel={(id) => authors.find((a) => a.id === id)?.name ?? id}
                    onChangeSelectedItem={handleChange}
                    name={'authorId'}
                    disabled={lockAuthor}
                    errorMessage={errors.authorId && touched.authorId ? (errors.authorId as string) : undefined}
                    placeholder={'Select the mentor'}>
                    {authors.map((author) => (
                      <Components.MenuItem key={author.id} value={author.id}>
                        {author.name}
                      </Components.MenuItem>
                    ))}
                  </ProjectSelect>
                </Components.FieldWrapper>

                <Components.FieldWrapper>
                  <Components.FieldLabel>Title</Components.FieldLabel>
                  <ProjectTextField
                    value={values.title}
                    onChangeText={handleChange}
                    name={'title'}
                    errorMessage={errors.title && touched.title ? (errors.title as string) : undefined}
                    placeholder={'e.g. My #1 daily habit'}
                  />
                </Components.FieldWrapper>
              </Components.FieldsContent>
            </Components.FieldsWrapper>

            <Components.FieldsWrapper>
              <Components.FieldStartAdornment variant={'readyToPlay'} />
              <Components.FieldsContent>
                <Components.FieldWrapper>
                  <Components.FieldLabel>Video (vertical, 9:16 recommended)</Components.FieldLabel>
                  <Components.UploadFileWrapper>
                    <Components.InputMenu
                      type='file'
                      accept={'video/*'}
                      id='mentorShortVideoFile'
                      onChange={(event) => onSelectVideo(event, setFieldValue)}
                    />
                    <ProjectTextField value={videoPath || ''} disabled placeholder={'No video uploaded'} />
                    {videoPath ? (
                      <>
                        <Components.IconWrapper
                          onClick={() => getPresignedUrl(videoPath, (url) => window.open(url, '_blank'))}>
                          <AccessLinkIcon />
                        </Components.IconWrapper>
                        <Components.ClearButton onClick={() => setVideoPath(undefined)}>Clear</Components.ClearButton>
                      </>
                    ) : (
                      <Components.UploadButton htmlFor={'mentorShortVideoFile'}>
                        {typeof videoProgress === 'number' ? `${videoProgress}%` : 'Upload Video'}
                      </Components.UploadButton>
                    )}
                  </Components.UploadFileWrapper>
                </Components.FieldWrapper>

                <Components.FieldWrapper>
                  <Components.FieldLabel>Length (seconds) — filled automatically from the video</Components.FieldLabel>
                  <ProjectTextField
                    value={values.durationSeconds?.toString() ?? ''}
                    onChangeText={handleChange}
                    name={'durationSeconds'}
                    errorMessage={
                      errors.durationSeconds && touched.durationSeconds ? (errors.durationSeconds as string) : undefined
                    }
                    placeholder={'e.g. 58'}
                    {...textFieldUtils.inputPositiveNumber}
                  />
                </Components.FieldWrapper>

                <Components.FieldWrapper>
                  <Components.FieldLabel>
                    Cover image (optional — defaults to the mentor&apos;s photo)
                  </Components.FieldLabel>
                  <Components.UploadFileWrapper>
                    <Components.InputMenu
                      type='file'
                      accept={'image/*'}
                      id='mentorShortThumbnailFile'
                      onChange={onSelectThumbnail}
                    />
                    <ProjectTextField value={thumbnailPath || ''} disabled placeholder={'No cover image'} />
                    {thumbnailPath ? (
                      <>
                        <Components.IconWrapper onClick={() => window.open(PUBLIC_BUCKET + thumbnailPath, '_blank')}>
                          <AccessLinkIcon />
                        </Components.IconWrapper>
                        <Components.ClearButton onClick={() => setThumbnailPath(undefined)}>
                          Clear
                        </Components.ClearButton>
                      </>
                    ) : (
                      <Components.UploadButton htmlFor={'mentorShortThumbnailFile'}>
                        {typeof thumbnailProgress === 'number' ? `${thumbnailProgress}%` : 'Upload Image'}
                      </Components.UploadButton>
                    )}
                  </Components.UploadFileWrapper>
                </Components.FieldWrapper>
              </Components.FieldsContent>
            </Components.FieldsWrapper>

            <Components.FieldsWrapper>
              <Components.FieldStartAdornment variant={'additional'} />
              <Components.FieldsContent>
                <Components.CheckboxFieldWrapper>
                  <Components.Checkbox
                    checked={values.isActive}
                    onChange={(e) => setFieldValue('isActive', e.target.checked)}
                    name={'isActive'}
                  />
                  <Components.CheckboxLabel>Active (visible in the app)</Components.CheckboxLabel>
                </Components.CheckboxFieldWrapper>
              </Components.FieldsContent>
            </Components.FieldsWrapper>
          </Components.ModalBodyContainer>
          <Components.ModalButtonsContainer>
            <Components.ModalTertiaryButton onClick={() => onCancel?.()}>Cancel</Components.ModalTertiaryButton>
            <Components.ModalPrimaryButton type={'submit'} disabled={typeof videoProgress === 'number'}>
              Save
            </Components.ModalPrimaryButton>
          </Components.ModalButtonsContainer>
        </Components.Form>
      )}
    </Formik>
  );
};

const CreateOrEditMentorShortModal = ({
  isModalOpen,
  onModalClose,
  modalState,
  authors,
  defaultAuthorId,
  lockAuthor,
}: {
  isModalOpen: boolean;
  onModalClose: (refreshTrigger?: boolean) => void;
  modalState?: { clip?: MentorShort };
  authors: Author[];
  defaultAuthorId?: string;
  lockAuthor?: boolean;
}) => {
  const { setIsLoading } = useContext(ActivityIndicatorContext);
  const { showErrorToast, showSuccessToast, showWarningToast } = useContext(ToastContext);
  const confirmationModal = useModalState<boolean>();
  // Remount the form per opened clip so its upload state starts fresh.
  const [formKey, setFormKey] = useState(0);
  useEffect(() => {
    if (isModalOpen) {
      setFormKey((k) => k + 1);
    }
  }, [isModalOpen]);

  const clip = modalState?.clip;

  const onSave = async (values: FormikValues, media: { videoStreamPath?: string; thumbnailPath?: string }) => {
    if (!media.videoStreamPath) {
      showWarningToast('Upload the video before saving');
      return;
    }
    setIsLoading(true);
    // New shorts (or ones moved to another mentor) go last for that mentor; otherwise keep the existing order.
    let order = clip?.order ?? 0;
    if (!clip?.id || clip.authorId !== values.authorId) {
      try {
        order = await getNextMentorShortOrder(values.authorId);
      } catch (e) {
        setIsLoading(false);
        showErrorToast(getProjectErrorFromRawError(e as Error));
        return;
      }
    }
    const data: CreateMentorShortDto = {
      authorId: values.authorId,
      title: String(values.title).trim(),
      videoStreamPath: media.videoStreamPath,
      thumbnailPath: media.thumbnailPath ?? null,
      durationSeconds: values.durationSeconds,
      order,
      isActive: values.isActive,
    };
    const result = clip?.id ? await updateMentorShort({ ...data, id: clip.id }) : await createMentorShort(data);
    setIsLoading(false);
    const error = isProjectError(result);
    if (error) {
      showErrorToast(error);
      return;
    }
    showSuccessToast(clip?.id ? 'The changes were saved successfully' : 'You successfully added a video');
    onModalClose(true);
  };

  return (
    <>
      <ProjectModal
        bodyChildren={
          <ModalBody
            key={formKey}
            modalState={clip}
            authors={authors}
            defaultAuthorId={defaultAuthorId}
            lockAuthor={lockAuthor}
            onSave={onSave}
            onCancel={() => confirmationModal.open(false)}
          />
        }
        isModalOpen={isModalOpen}
        onModalClose={() => confirmationModal.open(false)}
        modalTitle={clip ? 'Edit video' : 'Add video'}
        titleSuffix={clip ? `(ID: ${clip.id})` : undefined}
        disableCloseOnClickAway
      />
      <ConfirmationModal
        {...confirmationModal}
        confirmationText={confirmationTexts.lessons.cancelEditing}
        onConfirm={(value: boolean) => onModalClose(value)}
      />
    </>
  );
};

export default CreateOrEditMentorShortModal;
